import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    Dropdown,
    Empty,
    Grid,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Menu,
    Divider,
    Typography,
    InputNumber, Popover
} from "antd";
import { t } from "i18next";
import { useHistory, useLocation } from "react-router-dom";
import {
    PlusOutlined,
    FileExcelOutlined,
    EditOutlined,
    DeleteOutlined,
    AppstoreOutlined,
    BankOutlined,
    TagsOutlined,
    MedicineBoxOutlined,
    CloseOutlined,
    PhoneOutlined,
    MailOutlined,
    EnvironmentOutlined,
} from "@ant-design/icons";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import { FilterActions, FilterCard, SearchCard } from "../../core/components/quick-filter/QuickFilterParts";
import Helper from "../../core/Helper";
import ApiService from "../../core/service/ApiService";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";

export default function OperationList() {
    const history = useHistory();
    const location = useLocation();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    // Search modal'dan veya başka yerden ?institutionId=X ile gelinmiş olabilir.
    // O zaman dropdown'u o kuruma set ediyoruz + üstte info card gösteriyoruz.
    const queryInstitutionId = Helper.UrlParams.getUrlParam(location.search, 'institutionId');

    const [operations, setOperations] = useState([]);
    const [filter, setFilter] = useState({
        text: null,
        unitId: -1
    })
    const [institutions, setInstitutions] = useState([]);
    const [selectedInstitution, setSelectedInstitution] = useState(
        queryInstitutionId ? `K${queryInstitutionId}` : 'C'
    );
    const [pinnedInstitution, setPinnedInstitution] = useState(null);
    const [filterText, setFilterText] = useState('')
    const [prices, setPrices] = useState([])
    const [units, setUnits] = useState([])
    const { firmSettings, user, hasPermission } = useUser();

    // Pinned kurum bilgisini fetch et (üstteki info card için)
    useEffect(() => {
        if (!queryInstitutionId) {
            setPinnedInstitution(null);
            return;
        }
        ApiService.Institution.GetInstitution(queryInstitutionId).then(res => {
            if (res.success) setPinnedInstitution(res.data);
        });
    }, [queryInstitutionId]);

    const clearPinnedInstitution = () => {
        setPinnedInstitution(null);
        setSelectedInstitution('C');
        history.replace('/app/system/operations');
    };

    const getOperations = async () => {
        const typeRes = await ApiService.Appointment.GetAppointmentTypes();
        await Promise.all([
            ApiService.Institution.GetInstitutionList(true).then(res => {
                if (res.success) {
                    const data = [
                        { name: t('Current Operation Prices'), id: 'C' },
                        ...(typeRes.data.map((item) => {
                            item.id = `RT${item.id}`
                            return item;
                        })),
                        ...(res.data.map(x => {
                            x.id = `K${x.id}`
                            return x;
                        }))
                    ]
                    setInstitutions(data)
                }
            }),
            ApiService.Operation.GetAllOperationList().then(res => {
                if (res.success) {
                    setOperations(res.data)
                }
            }),
            ApiService.Unit.GetUnitList().then(res => {
                if (res.success) {
                    setUnits([
                        { id: -1, name: t('All') },
                        ...res.data.map(x => ({ ...x, name: `${x?.firm?.name} - ${x.name}` }))
                    ])
                }
            }),
        ])
    }

    const deleteOperation = async (operationId) => {
        const res = await ApiService.Operation.DeleteOperation(operationId);
        if (res.success) {
            await getOperations();
        }
    }

    const downloadExcel = async () => {
        const res = await ApiService.Operation.GetExcelPriceList(selectedInstitution)
        if (res.success) {
            Helper.File.saveFile({
                blobLink: res.data,
                fileName: res.fileName
            })
        }
    }

    const uploadExcel = async (file) => {
        const res = await ApiService.Operation.UpdatePriceFromExcel({
            excelBase64: file,
            institutionId: selectedInstitution
        })

        if (res.success) {
            await getOperations()
        }
    }

    const getInstitutionPriceList = async () => {
        const res = await ApiService.Operation.GetInstitutionPriceList(selectedInstitution.replaceAll('K', ''), operations.map(x => x.id));
        if (res.success) {
            setPrices(res.data)
        }
    }

    const getAppointmentTypePriceList = async () => {
        const res = await ApiService.Operation.GetAppointmentTypePriceList(selectedInstitution.replaceAll('RT', ''), operations.map(x => x.id));
        if (res.success) {
            setPrices(res.data)
        }
    }

    const renderAction = (row) => {
        if (!hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER)) {
            return ``
        }

        return (
            <Space>
                <a key="edit" onClick={() => history.push(`/app/system/operations/edit/${row.id}`)}>{t('edit')}</a>
                <a key="delete" onClick={async () => { await deleteOperation(row.id) }} style={{ color: 'red' }}>{t('Delete')}</a>
            </Space>
        )
    }

    useEffect(() => {
        if (selectedInstitution.startsWith('K')) {
            getInstitutionPriceList()
        }
        else if (selectedInstitution.startsWith('RT')) {
            getAppointmentTypePriceList()
        }
        else {
            setPrices([])
        }
    }, [selectedInstitution]);

    const filterData = operations.filter(x => (filter.unitId === -1 || x?.unit?.id === filter.unitId))
        .filter(x => !filter.text || (
            Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(filter.text)) ||
            Helper.Text.normalizeText(x.customId || '').includes(Helper.Text.normalizeText(filter.text)) ||
            Helper.Text.normalizeText(x.sutCodePart1 || '').includes(Helper.Text.normalizeText(filter.text)) ||
            Helper.Text.normalizeText(x.loinc || '').includes(Helper.Text.normalizeText(filter.text)) ||
            Helper.Text.normalizeText(user.tags.filter(t => x.tagList.includes(t.id)).map(t => t.name).join(' ')).includes(Helper.Text.normalizeText(filter.text))
        )
        )

    const renderText = (text, width) => {
        return (
            <Popover content={text}>
                <Typography.Text style={{ width: width }} ellipsis>{text}</Typography.Text>
            </Popover>
        )
    }

    const getColumns = () => {
        let columns = [
            { key: 'unit', dataIndex: 'unit', title: t('Unit'), width: 100, render: (text, row) => row.unit.name },
            { key: 'firmName', dataIndex: 'firmName', title: t('Firm'), render: (text, row) => row?.unit?.firm?.name, width: 100 },
            { key: 'name', dataIndex: 'name', title: t('Name'), width: 180 },
            {
                key: 'price',
                dataIndex: 'price',
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {t('Price')}
                        <Typography.Text style={{ width: 120 }} ellipsis={true}>
                            <b>({institutions.find(x => x.id === selectedInstitution)?.name})</b>
                        </Typography.Text>
                    </div>
                ),
                render: (text, row) => {
                    if (hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER)) {
                        return (
                            <InputNumber
                                size={'small'}
                                min={0}
                                value={prices?.length > 0 ? prices.find(x => x.operationId === row.id)?.price : text}
                                onPressEnter={async (e) => {
                                    const newPrice = e.target.value;
                                    const res = await ApiService.Operation.UpdateOperationPrice(row.id, selectedInstitution, newPrice);
                                    if (res.success) {
                                        setPrices(prices.map(x => {
                                            if (x.operationId === row.id) {
                                                return {
                                                    ...x,
                                                    price: newPrice
                                                }
                                            }
                                            return x
                                        }))
                                    }
                                }}
                            />
                        )
                    }

                    if (prices.length === 0)
                        return Helper.Currency.format(text)

                    const price = prices.find(x => x.operationId === row.id)
                    if (price)
                        return Helper.Currency.format(price.price)

                },
                width: 225
            },
            {
                key: 'tagList',
                dataIndex: 'tagList',
                title: t('Tags'),
                render: (tags, row) => {
                    return user?.tags?.filter(x => tags.includes(x.id))?.map(t => <Tag key={t.id} color={t.color}>{t.name}</Tag>)
                }
            },
            { key: 'customId', dataIndex: 'customId', width: 160, title: t('Custom ID'), render: (text, row) => renderText(row.customId || '-', 150) },
            { key: 'sutCodePart1', dataIndex: 'sutCodePart1', width: 150, title: t('SUT CODE-1'), render: (text, row) => row.sutCodePart1 || '-' },
            { key: 'loinc', dataIndex: 'loinc', width: 150, title: t('Loinc'), render: (text, row) => row.loinc || '-' },
            { key: 'huv', dataIndex: 'huv', width: 150, title: t('HUV'), render: (text, row) => renderText(row.huv, 140) },
            { key: 'ttb', dataIndex: 'ttb', width: 150, title: t('TTB'), render: (text, row) => renderText(row.ttb, 140) },
            { key: '#', dataIndex: '#', title: '#', render: (text, row) => renderAction(row) },
        ]

        if (!hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER)) {
            columns = columns.filter(x => !['customId', 'sutCodePart1', 'sutCodePart2'].includes(x.key))
        }

        return columns;
    }

    useEffect(() => {
        getOperations()
    }, []);

    // --- MOBİL KART RENDER ---
    const renderMobileCard = (row) => {
        const opTags = user?.tags?.filter(x => row.tagList?.includes(x.id)) || [];
        const currentPrice = prices?.length > 0 ? prices.find(x => x.operationId === row.id)?.price : row.price;
        const canEdit = hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER);

        return (
            <Card
                key={row.id}
                size="small"
                bodyStyle={{ padding: 12 }}
                style={{
                    borderRadius: 6,
                    borderLeft: '4px solid #1890ff',
                    marginBottom: 10
                }}
            >
                {/* ÜST: Ad + Edit/Delete */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#262626', marginBottom: 2 }}>
                            {row.name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#8c8c8c', flexWrap: 'wrap' }}>
                            <MedicineBoxOutlined />
                            <span>{row.unit?.name}</span>
                            {row?.unit?.firm?.name && (
                                <>
                                    <span>•</span>
                                    <BankOutlined />
                                    <span>{row.unit.firm.name}</span>
                                </>
                            )}
                        </div>
                    </div>
                    {canEdit && (
                        <Space size={4}>
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => history.push(`/app/system/operations/edit/${row.id}`)}
                            />
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={async () => {
                                    Modal.confirm({
                                        title: t('Delete'),
                                        content: `${row.name} silinsin mi?`,
                                        okText: t('Yes'),
                                        cancelText: t('No'),
                                        onOk: async () => { await deleteOperation(row.id) }
                                    });
                                }}
                            />
                        </Space>
                    )}
                </div>

                {/* TAGS */}
                {opTags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                        {opTags.map(tg => (
                            <Tag key={tg.id} color={tg.color} style={{ fontSize: 11, padding: '0 6px', margin: 0 }}>
                                {tg.name}
                            </Tag>
                        ))}
                    </div>
                )}

                {/* PRICE — seçili kuruma göre, edit'lenebilir */}
                <div style={{
                    background: '#fafafa',
                    padding: 8,
                    borderRadius: 4,
                    marginBottom: 8
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase' }}>
                                {institutions.find(x => x.id === selectedInstitution)?.name || t('Price')}
                            </div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#fa8c16' }}>
                                {Helper.Currency.format(currentPrice || 0)}
                            </div>
                        </div>
                        {canEdit && (
                            <InputNumber
                                size="middle"
                                min={0}
                                value={currentPrice}
                                style={{ width: 110, flexShrink: 0 }}
                                onPressEnter={async (e) => {
                                    const newPrice = e.target.value;
                                    const res = await ApiService.Operation.UpdateOperationPrice(row.id, selectedInstitution, newPrice);
                                    if (res.success) {
                                        setPrices(prices.map(x => {
                                            if (x.operationId === row.id) {
                                                return { ...x, price: newPrice }
                                            }
                                            return x
                                        }))
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* SUT / LOINC / HUV / TTB — kompakt 2x2 grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 6,
                    fontSize: 11
                }}>
                    {canEdit && row.customId && (
                        <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ color: '#8c8c8c' }}>{t('Custom ID')}</div>
                            <div style={{ color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.customId}</div>
                        </div>
                    )}
                    {row.sutCodePart1 && (
                        <div>
                            <div style={{ color: '#8c8c8c' }}>SUT</div>
                            <div style={{ color: '#595959' }}>{row.sutCodePart1}</div>
                        </div>
                    )}
                    {row.loinc && (
                        <div>
                            <div style={{ color: '#8c8c8c' }}>LOINC</div>
                            <div style={{ color: '#595959' }}>{row.loinc}</div>
                        </div>
                    )}
                    {row.huv && (
                        <div>
                            <div style={{ color: '#8c8c8c' }}>HUV</div>
                            <div style={{ color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.huv}</div>
                        </div>
                    )}
                    {row.ttb && (
                        <div>
                            <div style={{ color: '#8c8c8c' }}>TTB</div>
                            <div style={{ color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.ttb}</div>
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    // --- FİLTRE ÇUBUĞU (randevu listesiyle aynı düzen: arama + filtre kartları, altında eylemler) ---
    const renderFilters = () => {
        const institutionSelect = (
            <Select
                size="small"
                value={selectedInstitution}
                showSearch
                style={{ width: '100%' }}
                filterOption={(input, option) => { setFilterText(input); return true; }}
                onInputKeyDown={(e) => { if (e.code === 'Escape') setFilterText(''); }}
                onBlur={() => setFilterText('')}
                onChange={(val) => setSelectedInstitution(val)}
            >
                <Select.OptGroup label={t('Operation')}>
                    {institutions.filter(x => Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(filterText))).filter(x => x.id.startsWith('C')).map(x => (
                        <Select.Option key={x.id} value={x.id}>{x.name}</Select.Option>
                    ))}
                </Select.OptGroup>
                <Select.OptGroup label={t('Appointment Type')}>
                    {institutions.filter(x => Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(filterText))).filter(x => x.id.startsWith('RT')).map(x => (
                        <Select.Option key={x.id} value={x.id}>{x.name}</Select.Option>
                    ))}
                </Select.OptGroup>
                <Select.OptGroup label={'Kurum'}>
                    {institutions.filter(x => Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(filterText))).filter(x => x.id.startsWith('K')).map(x => (
                        <Select.Option key={x.id} value={x.id}>{x.name}</Select.Option>
                    ))}
                </Select.OptGroup>
            </Select>
        );

        const excelDropdown = (hasPermission(PERMISSIONS.FIRM_INSTITUTION_MANAGER) || user.institutionFirms.map(x => x.institutionId).includes(selectedInstitution)) && (
            <Dropdown
                overlay={
                    <Menu>
                        <Menu.Item key="1" onClick={downloadExcel}>
                            {t('Export Excel')}
                        </Menu.Item>
                        <Menu.Item key="3" onClick={async () => {
                            const resFile = await Helper.File.getFileAsBase64({ accept: '*.xlsx' });
                            if (resFile.data) {
                                Modal.confirm({
                                    closable: true,
                                    onOk: async () => { await uploadExcel(resFile.data, 1) },
                                    onCancel: async () => { },
                                    okText: t('Yes'),
                                    cancelText: t('No'),
                                    title: t('Update Operation Prices'),
                                    content: t('Do you want to continue to update operation prices?')
                                })
                            }
                        }}>
                            {t('Import Excel')}
                        </Menu.Item>
                    </Menu>
                }
            >
                <Button
                    size="small"
                    type="primary"
                    icon={<FileExcelOutlined />}
                >
                    Excel
                </Button>
            </Dropdown>
        );

        const priceUpdateBtn = hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER) && (
            <Button
                size="small"
                type="primary"
                onClick={() => history.push('/app/system/operations/price-update')}
            >
                {t('Price Update')}
            </Button>
        );

        return (
            <>
                <div className="qf-row qf-row-top">
                    <SearchCard
                        value={filter.text ?? ''}
                        onChange={(text) => setFilter({ ...filter, text })}
                        placeholder={`${t('Search')}: ${t('Name')}, ${t('SUT')}, ${t('Tags')}`}
                    />
                    <FilterCard label={t('Unit')}>
                        <Select
                            style={{ width: '100%' }}
                            showSearch
                            placeholder={t('Unit')}
                            value={filter.unitId}
                            size="small"
                            filterOption={(input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))}
                            onChange={(val) => setFilter({ ...filter, unitId: val })}
                            fieldNames={{ label: 'name', value: 'id' }}
                            options={units}
                        />
                    </FilterCard>
                    <FilterCard label={t('Institution')}>
                        {institutionSelect}
                    </FilterCard>
                </div>
                <FilterActions>
                    <Button size="small" icon={<PlusOutlined />} type="primary" onClick={() => history.push('/app/system/operations/create')}>
                        {t('New')}
                    </Button>
                    {excelDropdown}
                    {priceUpdateBtn}
                </FilterActions>
            </>
        );
    };

    const renderPinnedInstitutionCard = () => {
        if (!pinnedInstitution) return null;
        return (
            <Card
                size="small"
                style={{
                    marginBottom: 12,
                    borderLeft: '4px solid #fa8c16',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                }}
                bodyStyle={{ padding: 12 }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: '#fa8c1622', color: '#fa8c16',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 20, flexShrink: 0,
                    }}>
                        <BankOutlined />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#fa8c16', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {t('Institution')}
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#262626', marginBottom: 4 }}>
                            {pinnedInstitution.name}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: '#8c8c8c' }}>
                            {pinnedInstitution.address && (
                                <span><EnvironmentOutlined /> {pinnedInstitution.address}</span>
                            )}
                            {pinnedInstitution.phoneNumber && (
                                <span><PhoneOutlined /> {pinnedInstitution.phoneNumber}</span>
                            )}
                            {pinnedInstitution.mail && (
                                <span><MailOutlined /> {pinnedInstitution.mail}</span>
                            )}
                            {pinnedInstitution.appointmentType?.name && (
                                <span>• {pinnedInstitution.appointmentType.name}</span>
                            )}
                        </div>
                    </div>
                    <Button
                        size="small"
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={clearPinnedInstitution}
                        title={t('Clear')}
                    />
                </div>
            </Card>
        );
    };

    return (
        <SimpleLayout fullWidth={true}>
            {renderPinnedInstitutionCard()}
            {renderFilters()}
            <Card bodyStyle={{ padding: isMobile ? 12 : 24 }}>

                {isMobile ? (
                    // MOBİL: Kart listesi
                    <div>
                        {filterData.length === 0 ? (
                            <Empty description={t('No Data')} style={{ padding: 32 }} />
                        ) : (
                            <>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                                    {filterData.length} kayıt
                                </div>
                                {filterData.slice(0, 50).map(row => renderMobileCard(row))}
                                {filterData.length > 50 && (
                                    <div style={{ textAlign: 'center', padding: 12, color: '#8c8c8c', fontSize: 12 }}>
                                        İlk 50 kayıt gösteriliyor. Aramayı daraltın.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    // DESKTOP: Mevcut Table
                    <Table
                        size={'small'}
                        dataSource={filterData}
                        rowKey={'id'}
                        pagination={{ pageSize: 25, size: 'small' }}
                        scroll={{ y: window.innerHeight - 200 }}
                        columns={getColumns()}
                    />
                )}
            </Card>
        </SimpleLayout>
    )
}
